import { isUndefined, noop } from 'lodash';
import {
  createContext,
  Dispatch,
  ReactNode,
  SetStateAction,
  useCallback,
  useMemo,
  useState,
} from 'react';
import { useFieldArray, UseFieldArrayReturn } from 'react-hook-form';

import { FormProvider, useForm } from '@/components/react-hook-form';
import { useGuardedContext } from '@/hooks/useGuardedContext';
import {
  PresentationBundleKind,
  PresentationTemplateType,
} from '@/types/schema';
import { UnreachableError } from '@/utils/errors';

import {
  PresentationTemplateEditorErrorState,
  PresentationTemplateEditorLoadingState,
} from './PresentationTemplateEditor.components';
import {
  PresentationTemplateEditorFormShape,
  PresentationTemplateEditorStage,
} from './PresentationTemplateEditor.types';
import { BundleConfigModalSwitcher } from './stages/presentationTemplate/BundleConfigModalSwitcher';
import { PresentationTemplateStageEditor } from './stages/presentationTemplate/PresentationTemplateStageEditor';
import { PresentationTemplateSlideEditor } from './stages/slideEditor/PresentationTemplateSlideEditor';

export interface BundleConfigModalState {
  kind: PresentationBundleKind;
  /** null for create mode, index for edit mode */
  bundleIndex: number | null;
}

export interface PresentationTemplateEditorContext {
  body: ReactNode;
  setStage: (
    stage: PresentationTemplateEditorStage,
    selectedBundleId?: string
  ) => void;
  /**
   * Common wrapper around useFormArray, so both the sidebar and main bundle grid can use it.
   * The third type parameter '_rhfId' specifies the custom key name for react-hook-form's
   * auto-generated ID, avoiding collision with the bundle's actual `id` field.
   */
  bundlesFormArray: UseFieldArrayReturn<
    PresentationTemplateEditorFormShape,
    'bundles',
    '_rhfId'
  >;
  /**
   * Marks the parent form's `bundles` field dirty. Call this after using
   * `bundlesFormArray.update(...)` (or `.replace(...)`) because those don't
   * propagate dirty state to the form (`_setFieldArray` is invoked with
   * `shouldUpdateFieldsAndState=false`), so the "Save changes" button stays
   * disabled otherwise.
   */
  markBundlesDirty: () => void;
  onClose: () => void;
  selectedBundleId: string | null;

  setIsSidebarExpanded: Dispatch<SetStateAction<boolean>>;
  isSidebarExpanded: boolean;

  /**
   * When true, the loaded template is treated as a read-only source: the editor
   * only offers "Save as new template" (which creates a new template of
   * `createTemplateType`) instead of updating in place. Used when opening a
   * template the current context can't edit directly — a firm template from the
   * client list, or a system template (e.g. Estate360).
   */
  isDuplicateFlow: boolean;

  /**
   * When true, we are creating a new template rather than editing an existing one.
   * The template has not yet been persisted to the backend.
   */
  isCreating: boolean;

  /**
   * The template type to use when creating a new template (via "save as new" or
   * the create flow). Determined by which page the editor is opened from:
   * admin page → TenantTemplate, user page → UserTemplate.
   */
  createTemplateType: PresentationTemplateType;

  /**
   * Optional callback invoked after a successful save (create or update).
   * When provided, this is called instead of onClose after saving.
   */
  onSaveComplete?: () => void;

  /** Opens a bundle configuration modal. Omit bundleIndex for create mode. */
  openBundleConfigModal: (
    kind: PresentationBundleKind,
    bundleIndex?: number
  ) => void;
  closeBundleConfigModal: () => void;
  bundleConfigModalState: BundleConfigModalState | null;
}

const PresentationTemplateEditorContext =
  createContext<PresentationTemplateEditorContext>({
    body: null,
    bundlesFormArray: {
      fields: [],
      append: noop,
      remove: noop,
      move: noop,
      replace: noop,
      insert: noop,
      swap: noop,
      prepend: noop,
      update: noop,
    },
    markBundlesDirty: noop,
    setStage: () => {},
    onClose: () => {},
    selectedBundleId: null,

    setIsSidebarExpanded: noop,
    isSidebarExpanded: true,
    isDuplicateFlow: false,
    isCreating: false,
    createTemplateType: PresentationTemplateType.TenantTemplate,
    onSaveComplete: undefined,

    openBundleConfigModal: noop,
    closeBundleConfigModal: noop,
    bundleConfigModalState: null,
  });

const defaultValues: PresentationTemplateEditorFormShape = {
  id: '',
  displayName: '',
  bundles: [],
  isSystemTemplate: false,
};

export interface PresentationTemplateEditorContextProviderProps {
  children: ReactNode;
  onClose: () => void;
  onSaveComplete?: () => void;
  isDuplicateFlow?: boolean;
  isCreating?: boolean;
  createTemplateType: PresentationTemplateType;
}

const BUNDLES_WITH_CONFIG: PresentationBundleKind[] = [
  PresentationBundleKind.WaterfallOverviewBundle,
  PresentationBundleKind.BeneficiaryReportBundle,
  PresentationBundleKind.WaterfallComparisonBundle,
  PresentationBundleKind.EstateInsightsBundle,
  PresentationBundleKind.DocumentSummaryBundle,
  PresentationBundleKind.EntitySummaryBundle,
];

/**
 * Returns a predicate that checks whether a bundle kind should open a
 * configuration modal rather than being immediately added to the template.
 */
export function useBundleKindRequiresConfigModal(): (
  kind: PresentationBundleKind
) => boolean {
  return useCallback(
    (kind: PresentationBundleKind) => BUNDLES_WITH_CONFIG.includes(kind),
    []
  );
}

export function PresentationTemplateEditorContextProvider({
  children,
  onClose,
  onSaveComplete,
  isDuplicateFlow = false,
  isCreating = false,
  createTemplateType,
}: PresentationTemplateEditorContextProviderProps) {
  const [stage, setStageInternal] = useState<PresentationTemplateEditorStage>(
    PresentationTemplateEditorStage.Loading
  );
  const [selectedBundleId, setSelectedBundleId] = useState<string | null>(null);

  const body = useMemo<ReactNode>(() => {
    switch (stage) {
      case PresentationTemplateEditorStage.Loading:
        return <PresentationTemplateEditorLoadingState />;
      case PresentationTemplateEditorStage.Template:
        return <PresentationTemplateStageEditor />;
      case PresentationTemplateEditorStage.Page:
        return <PresentationTemplateSlideEditor />;
      case PresentationTemplateEditorStage.Error:
        return <PresentationTemplateEditorErrorState />;
      default:
        throw new UnreachableError({
          case: stage,
          message: `Unknown stage: ${stage}`,
        });
    }
  }, [stage]);

  const form = useForm<PresentationTemplateEditorFormShape>({
    defaultValues,
  });

  const bundlesFormArray = useFieldArray({
    control: form.control,
    name: 'bundles',
    // Use a different key name for react-hook-form's auto-generated ID
    // to avoid overwriting the bundle's actual `id` field
    keyName: '_rhfId',
  });

  const markBundlesDirty = useCallback(() => {
    form.setValue('bundles', form.getValues('bundles'), { shouldDirty: true });
  }, [form]);

  const [isSidebarExpanded, setIsSidebarExpanded] = useState<boolean>(true);

  const setStage = useCallback(
    (stage: PresentationTemplateEditorStage, selectedBundleId?: string) => {
      setStageInternal(stage);
      setSelectedBundleId(
        isUndefined(selectedBundleId) ? null : selectedBundleId
      );
      // Reset sidebar to expanded when entering the slide editor so pages are
      // always visible on entry
      if (stage === PresentationTemplateEditorStage.Page) {
        setIsSidebarExpanded(true);
      }
    },
    []
  );

  const [bundleConfigModalState, setBundleConfigModalState] =
    useState<BundleConfigModalState | null>(null);

  const openBundleConfigModal = useCallback(
    (kind: PresentationBundleKind, bundleIndex?: number) => {
      setBundleConfigModalState({
        kind,
        bundleIndex: bundleIndex ?? null,
      });
    },
    []
  );

  const closeBundleConfigModal = useCallback(() => {
    setBundleConfigModalState(null);
  }, []);
  return (
    <FormProvider {...form}>
      <PresentationTemplateEditorContext.Provider
        value={{
          body,
          setStage,
          onClose,
          bundlesFormArray,
          markBundlesDirty,
          selectedBundleId,
          isSidebarExpanded,
          setIsSidebarExpanded,
          isDuplicateFlow,
          isCreating,
          createTemplateType,
          onSaveComplete,
          openBundleConfigModal,
          closeBundleConfigModal,
          bundleConfigModalState,
        }}
      >
        {children}
        {bundleConfigModalState && (
          <BundleConfigModalSwitcher
            state={bundleConfigModalState}
            onClose={closeBundleConfigModal}
          />
        )}
      </PresentationTemplateEditorContext.Provider>
    </FormProvider>
  );
}

export function usePresentationTemplateEditorContext() {
  return useGuardedContext(
    PresentationTemplateEditorContext,
    'PresentationTemplateEditorContext'
  );
}
